import { motion } from "framer-motion";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup } from "react-leaflet";
import { Phone } from "lucide-react";
import "leaflet/dist/leaflet.css";

const HUB = [39.0837, -84.5086];

const CITIES = [
  { name: "Covington, KY", note: "Northern Kentucky", pos: [39.0837, -84.5086] },
  { name: "Newport, KY", pos: [39.0914, -84.4958] },
  { name: "Florence, KY", pos: [38.9989, -84.6266] },
  { name: "Cincinnati, OH", pos: [39.1031, -84.512] },
  { name: "Hamilton, OH", pos: [39.3995, -84.5613] },
  { name: "Mason, OH", pos: [39.3601, -84.3099] },
  { name: "Lawrenceburg, IN", pos: [39.0909, -84.8699] },
];

export const Coverage = () => {
  return (
    <section id="coverage" className="py-16 sm:py-24 lg:py-32" data-testid="coverage-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <p className="font-jbmono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold" data-testid="coverage-eyebrow">
              ✦ Coverage Zone
            </p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="coverage-heading">
              Local to Greater Cincinnati.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed" data-testid="coverage-statement">
              Serving Greater Cincinnati and surrounding Tri-State communities.
            </p>

            <div className="card-surface mt-8 rounded-xl p-6">
              <p className="text-sm text-slate-300 leading-relaxed">
                From downtown Cincinnati to the Northern Kentucky river cities and
                nearby Tri-State communities — if you're in the zone, we come to you.
              </p>
              <a
                href="tel:+15137205882"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#FF2A3B]/40 bg-[#FF2A3B]/10 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF2A3B]/20"
                data-testid="coverage-call-link"
              >
                <Phone size={15} className="text-[#FF2A3B]" />
                Not sure? Call (513) 720-5882
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-3"
          >
            <div className="h-[420px] sm:h-[500px] overflow-hidden rounded-xl border border-white/10" data-testid="coverage-map">
              <MapContainer
                center={HUB}
                zoom={9}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%", background: "#0A0B0E" }}
                attributionControl={true}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                  attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                />
                <Circle
                  center={HUB}
                  radius={45000}
                  pathOptions={{ color: "#FF2A3B", weight: 1.5, dashArray: "6 8", fillColor: "#FF2A3B", fillOpacity: 0.05 }}
                />
                {CITIES.map((c) => (
                  <CircleMarker
                    key={c.name}
                    center={c.pos}
                    radius={c.note ? 9 : 5}
                    pathOptions={{ color: "#FF2A3B", weight: 2, fillColor: "#FF2A3B", fillOpacity: c.note ? 0.9 : 0.6 }}
                  >
                    <Popup>
                      <strong>{c.name}</strong>
                      {c.note ? ` — ${c.note}` : ""}
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <p className="mt-4 text-center font-jbmono text-xs uppercase tracking-[0.18em] text-slate-500" data-testid="coverage-map-caption">
              Dashed ring = typical service radius • Outside the zone? Call and ask
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
